/**
 * Terugval van databank naar code.
 * Elke publieke pagina vraagt haar inhoud op; ontbreekt een waarde, dan blijft
 * de tekst uit de code staan. Zo kan er nooit een lege of halve pagina verschijnen.
 */
import type { ContentPage, ContentSection, SectionMedia } from "@/lib/content.functions";
import { createElement, type ReactNode } from "react";
import { addressLine, business, responseTime } from "@/content/site";
import { imageSrcSet, type DeliveryVariant } from "@/lib/image-delivery";
import type { FocalPoint } from "@/lib/media-taxonomy";
import { RichText } from "@/components/site/RichText";
import { isDocString, leesRijkeTekst, naarPlatteTekst } from "@/lib/rich-text";

/**
 * Centrale waarden blijven uit site.ts komen, ook midden in een lopende tekst.
 * De redacteur typt een sleutelwoord tussen accolades; de site vult het in bij het tonen.
 */
const TOKENS: Record<string, string> = {
  reactietermijn: responseTime.within,
  reactietijd: responseTime.short,
  telefoon: business.phone,
  email: business.email,
  adres: addressLine,
  werkgebied: "Limburg en Zuid-Nederland",
  werkgebiedKort: business.areaLabel,
  plaats: business.city,
  btw: business.vat,
};

export function vulTokens(tekst: string) {
  return tekst.replace(/\{(\w+)\}/g, (match, sleutel: string) => TOKENS[sleutel] ?? match);
}

export type Kaart = { titel: string; tekst: string };

/** Eén foto zoals de publieke pagina ze toont. */
export type Foto = { src: string; alt: string; caption?: string; variants?: DeliveryVariant[]; srcSet?: string | undefined; focal?: FocalPoint | null; decorative?: boolean; placeholder?: string | null };

export type PageContent = {
  section: (sleutel: string) => ContentSection | null;
  /** Eén tekstveld met terugval naar de code. */
  veld: (sleutel: string, veld: keyof ContentSection, terugval: string) => string;
  /** Rich text field rendered inline (inside an existing paragraph or text slot). */
  rijkInline: (sleutel: string, terugval: string) => ReactNode;
  /** Raw rich text value (plain text or document JSON) for <RichText>. */
  rijk: (sleutel: string, terugval: string) => string;
  /** Alinea's: de databanktekst wordt op lege regels gesplitst. */
  alineas: (sleutel: string, terugval: string[]) => string[];
  /** Opsomming met terugval naar de code. */
  lijst: (sleutel: string, terugval: string[]) => string[];
  /** Kaarten: elke regel is "Titel | tekst". */
  kaarten: (sleutel: string, terugval: Kaart[]) => Kaart[];
  /** Losse labels: elke regel is "sleutel | tekst". */
  label: (sleutel: string, key: string, terugval: string) => string;
  /** Eén foto bij een blok, met terugval naar de foto uit de code. */
  foto: (sleutel: string, terugvalSrc: string, terugvalAlt: string) => Foto;
  /** Fotoreeks bij een blok; leeg in de databank betekent de reeks uit de code. */
  fotos: (sleutel: string, terugval: Foto[]) => Foto[];
  /** Alle blokken van het type "fotos" die de redacteur zelf toevoegde. */
  fotoblokken: () => { sleutel: string; titel: string; overline: string; tekst: string; fotos: Foto[] }[];
  metaTitle: (terugval: string) => string;
  metaDescription: (terugval: string) => string;
  indexeerbaar: boolean;
};

function naarFotos(media: SectionMedia[], terugval: Foto[]): Foto[] {
  const fotos = media
    .filter((foto) => Boolean(foto.url))
    .map((foto) => ({ src: foto.url as string, alt: vulTokens(foto.alt), caption: vulTokens(foto.caption), variants: foto.variants ?? [], srcSet: imageSrcSet(foto.variants), focal: foto.focal ?? null, decorative: foto.decorative ?? false, placeholder: foto.placeholder ?? null }));
  return fotos.length > 0 ? fotos : terugval;
}

export function pageContent(page: ContentPage | null | undefined): PageContent {
  const map = new Map((page?.sections ?? []).map((section) => [section.sleutel, section]));

  const veld = (sleutel: string, key: keyof ContentSection, terugval: string) => {
    const value = map.get(sleutel)?.[key];
    if (typeof value !== "string" || value.trim() === "") return terugval;
    return vulTokens(isDocString(value) ? naarPlatteTekst(value) : value);
  };

  const rijen = (sleutel: string) =>
    (map.get(sleutel)?.opsomming ?? []).map((item) => item.trim()).filter((item) => item !== "");

  return {
    section: (sleutel) => map.get(sleutel) ?? null,
    veld,
    rijk: (sleutel, terugval) => {
      const tekst = map.get(sleutel)?.tekst ?? "";
      return tekst.trim() === "" ? terugval : tekst;
    },
    rijkInline: (sleutel, terugval) => {
      const tekst = map.get(sleutel)?.tekst ?? "";
      return createElement(RichText, { waarde: tekst.trim() === "" ? terugval : tekst, inline: true });
    },
    alineas: (sleutel, terugval) => {
      const tekst = map.get(sleutel)?.tekst ?? "";
      if (tekst.trim() === "") return terugval;
      // A document becomes one single-block document per paragraph, rendered by <RichText inline>.
      if (isDocString(tekst)) {
        return (leesRijkeTekst(tekst).content ?? []).map((blok) => JSON.stringify({ type: "doc", content: [blok] }));
      }
      return tekst
        .split(/\n\s*\n/)
        .map((deel) => vulTokens(deel.trim()))
        .filter(Boolean);
    },
    lijst: (sleutel, terugval) => {
      const items = rijen(sleutel).map(vulTokens);
      return items.length > 0 ? items : terugval;
    },
    kaarten: (sleutel, terugval) => {
      const items = rijen(sleutel);
      if (items.length === 0) return terugval;
      return items.map((regel) => {
        const [titel = "", ...rest] = regel.split("|");
        return { titel: vulTokens(titel.trim()), tekst: vulTokens(rest.join("|").trim()) };
      });
    },
    label: (sleutel, key, terugval) => {
      for (const regel of rijen(sleutel)) {
        const [naam = "", ...rest] = regel.split("|");
        if (naam.trim() === key) return vulTokens(rest.join("|").trim()) || terugval;
      }
      return terugval;
    },
    foto: (sleutel, terugvalSrc, terugvalAlt) => {
      const section = map.get(sleutel);
      const alt = section?.alt?.trim() ? vulTokens(section.alt) : terugvalAlt;
      return section?.mediaUrl ? { src: section.mediaUrl, alt, variants: section.mediaVariants ?? [], srcSet: imageSrcSet(section.mediaVariants), focal: section.mediaFocal ?? null } : { src: terugvalSrc, alt };
    },
    fotos: (sleutel, terugval) => naarFotos(map.get(sleutel)?.media ?? [], terugval),
    fotoblokken: () =>
      (page?.sections ?? [])
        .filter((section) => section.type === "fotos")
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((section) => ({
          sleutel: section.sleutel,
          titel: vulTokens(section.titel),
          overline: vulTokens(section.overline),
          tekst: section.tekst,
          fotos: naarFotos(section.media, []),
        })),
    metaTitle: (terugval) => (page?.metaTitle?.trim() ? vulTokens(page.metaTitle) : terugval),
    metaDescription: (terugval) => (page?.metaDescription?.trim() ? vulTokens(page.metaDescription) : terugval),
    indexeerbaar: page?.indexeerbaar ?? true,
  };
}
